import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js'

const UserPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID
const ClientId = import.meta.env.VITE_COGNITO_CLIENT_ID
if (!UserPoolId || !ClientId) {
  throw new Error(
    'Missing VITE_COGNITO_USER_POOL_ID or VITE_COGNITO_CLIENT_ID env vars',
  )
}
const POOL_DATA = { UserPoolId, ClientId }

export const userPool = new CognitoUserPool(POOL_DATA)

export function getCognitoUser(email: string): CognitoUser {
  return new CognitoUser({ Username: email, Pool: userPool })
}

/**
 * Discriminated result for authenticateUser:
 *
 *  - `session`              — normal successful sign-in; caller flips to logged-in
 *  - `newPasswordRequired`  — Cognito challenged us because the user is in
 *                             FORCE_CHANGE_PASSWORD status (admin-invited first
 *                             sign-in). Caller must render a "set new password"
 *                             screen and call completeNewPasswordChallenge with
 *                             the same `user` instance to complete the flow.
 */
export type AuthResult =
  | { kind: 'session'; session: CognitoUserSession }
  | {
      kind: 'newPasswordRequired'
      user: CognitoUser
      userAttributes: Record<string, string>
      requiredAttributes: string[]
    }

export function authenticateUser(
  email: string,
  password: string,
): Promise<AuthResult> {
  const user = getCognitoUser(email)
  const authDetails = new AuthenticationDetails({
    Username: email,
    Password: password,
  })

  return new Promise((resolve, reject) => {
    user.authenticateUser(authDetails, {
      onSuccess: (session) => resolve({ kind: 'session', session }),
      onFailure: (err) => reject(err),
      newPasswordRequired: (userAttributes, requiredAttributes) =>
        resolve({
          kind: 'newPasswordRequired',
          user,
          userAttributes,
          requiredAttributes: requiredAttributes ?? [],
        }),
    })
  })
}

/**
 * Complete a NEW_PASSWORD_REQUIRED challenge.
 *
 * `user` MUST be the CognitoUser instance returned from the matching
 * `authenticateUser` call (it holds the challenge state internally).
 *
 * `attributes` should be the userAttributes Cognito sent back, with the
 * read-only `email_verified` key removed (Cognito rejects re-sending it),
 * plus any required attributes the user filled in.
 */
export function completeNewPasswordChallenge(
  user: CognitoUser,
  newPassword: string,
  attributes: Record<string, string> = {},
): Promise<CognitoUserSession> {
  // Cognito rejects echoing back read-only attributes.
  // `email_verified` is the common offender; strip it defensively.
  const safeAttrs = { ...attributes }
  delete safeAttrs.email_verified
  delete safeAttrs.email // email is the username, not a re-settable attribute

  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(newPassword, safeAttrs, {
      onSuccess: (session) => resolve(session),
      onFailure: (err) => reject(err),
    })
  })
}

export function signUp(
  email: string,
  password: string,
): Promise<void> {
  const attributes = [
    new CognitoUserAttribute({ Name: 'email', Value: email }),
  ]

  return new Promise((resolve, reject) => {
    userPool.signUp(email, password, attributes, [], (err) => {
      if (err) return reject(err)
      resolve()
    })
  })
}

export function confirmRegistration(
  email: string,
  code: string,
): Promise<void> {
  const user = getCognitoUser(email)
  return new Promise((resolve, reject) => {
    user.confirmRegistration(code, true, (err) => {
      if (err) return reject(err)
      resolve()
    })
  })
}

export function forgotPassword(email: string): Promise<void> {
  const user = getCognitoUser(email)
  return new Promise((resolve, reject) => {
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    })
  })
}

export function confirmPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  const user = getCognitoUser(email)
  return new Promise((resolve, reject) => {
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    })
  })
}

export function getCurrentSession(): Promise<CognitoUserSession | null> {
  const user = userPool.getCurrentUser()
  if (!user) return Promise.resolve(null)

  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        resolve(null)
      } else {
        resolve(session)
      }
    })
  })
}

export function signOut(): void {
  const user = userPool.getCurrentUser()
  if (user) user.signOut()
}
